import { useQuery } from '@tanstack/react-query';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Icon } from '../components/icons';
import { importKeys, importsApi } from './api';

/**
 * One of the person's photos, through a link that lasts a few minutes, with the spot to look at marked.
 * Small beside the fields (tilted like a bill on a desk, as the canvas draws it); tap it to look closer
 * at the marked spot, and again for the whole photo.
 */
export function PhotoView({
  uploadId,
  number,
  area,
  tilt = false,
}: {
  uploadId: string;
  number: number;
  /** Where to look, as parts (0–1) of the photo's width and height. */
  area: { x: number; y: number; w: number; h: number } | null;
  tilt?: boolean;
}) {
  const { t } = useTranslation();
  const [open, setOpen] = useState(false);
  const [whole, setWhole] = useState(false);
  const link = useQuery({
    queryKey: importKeys.view(uploadId),
    queryFn: ({ signal }) => importsApi.view(uploadId, signal),
    // The link lasts 5 minutes; a fresh one a little before.
    staleTime: 4 * 60 * 1000,
  });
  const url = link.data?.url;
  const alt = t('imports.checks.photoAlt', { number });
  const mark = area ? (
    <span
      className="photo-view__mark"
      aria-hidden="true"
      style={{
        left: `${area.x * 100}%`,
        top: `${area.y * 100}%`,
        width: `${area.w * 100}%`,
        height: `${area.h * 100}%`,
      }}
    />
  ) : null;

  if (!url) {
    return (
      <div className={`photo-view photo-view--empty${tilt ? ' photo-view--tilt' : ''}`}>
        <span className="muted small">
          {link.isError ? t('imports.checks.photoGone') : t('imports.checks.photoLoading')}
        </span>
      </div>
    );
  }

  // Close up: the marked spot filling the width, at most 4 times as big.
  const zoom = area && !whole ? Math.min(4, Math.max(1, 0.85 / Math.max(area.w, 0.05))) : 1;
  const origin = area ? `${(area.x + area.w / 2) * 100}% ${(area.y + area.h / 2) * 100}%` : 'center';
  return (
    <>
      <button
        type="button"
        className={`photo-view${tilt ? ' photo-view--tilt' : ''}`}
        aria-label={t('imports.checks.zoom')}
        aria-expanded={open}
        onClick={() => setOpen((o) => !o)}
      >
        <span className="photo-view__frame">
          <img src={url} alt={alt} />
          {mark}
        </span>
        <span className="photo-view__zoom" aria-hidden="true">
          <Icon name="zoom-in" size={16} />
        </span>
      </button>
      {open ? (
        <div className="photo-zoom">
          <div className="photo-zoom__frame">
            <div className="photo-zoom__image" style={{ transform: `scale(${zoom})`, transformOrigin: origin }}>
              <img src={url} alt={alt} />
              {mark}
            </div>
          </div>
          <div className="photo-zoom__actions">
            {area ? (
              <button type="button" className="linkbtn" onClick={() => setWhole((w) => !w)}>
                {whole ? t('imports.checks.closeUp') : t('imports.checks.whole')}
              </button>
            ) : null}
            <button type="button" className="linkbtn" onClick={() => setOpen(false)}>
              {t('imports.checks.closePhoto')}
            </button>
          </div>
        </div>
      ) : null}
    </>
  );
}
